import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Checkbox from '@material-ui/core/Checkbox';

import config, {
  StudiotelConfig,
  RadioList,
} from '../../../config/studiotel.config';

import { OxeDeviceConfig, StudiotelConfigFile } from '../stepper.component';

import StepperLayout from '../layout.component';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'flex-start',
    flex: 1,
    maxHeight: `calc(100vh - ${350}px)`,
    overflow: 'auto',
    '& .MuiInputBase-root': {
      maxWidth: 380,
    },
  },
  redundancyCheckbox: {
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  formsRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(4),
    alignItems: 'flex-start',
  },
  formColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  deviceLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
}));

interface IProps {
  hintAction: React.ReactNode;
  action: React.ReactNode;
  setStepValid: React.Dispatch<React.SetStateAction<boolean>>;
  studiotelConfigFile: StudiotelConfigFile;
  setStudiotelConfigFile: React.Dispatch<
    React.SetStateAction<StudiotelConfigFile>
  >;
  version: string | undefined;
}

function isOxeDeviceValid(device: OxeDeviceConfig): boolean {
  return !!(
    device.login &&
    device.password &&
    device.port &&
    device.hostname &&
    device.nb_att &&
    device.nb_pre &&
    device.nb_rep
  );
}

export default function HardwareSetup(props: IProps) {
  const {
    hintAction,
    action,
    setStepValid,
    studiotelConfigFile,
    setStudiotelConfigFile,
    version,
  } = props;
  // Normalisation : ALCATEL_OXE_REDUNDANT est traité comme ALCATEL_OXE pour le radio button
  const [studiotelConfig, setStudiotelConfig] = React.useState<StudiotelConfig>(
    studiotelConfigFile.studiotel.type === StudiotelConfig.ALCATEL_OXE_REDUNDANT
      ? StudiotelConfig.ALCATEL_OXE
      : studiotelConfigFile.studiotel.type
  );

  const isOxeRedundant = studiotelConfigFile?.studiotel?.type === StudiotelConfig.ALCATEL_OXE_REDUNDANT;

  React.useEffect(() => {
    const { studiotel } = studiotelConfigFile;

    if (studiotel.type !== StudiotelConfig.NONE) {
      if (studiotel.type === StudiotelConfig.LIVEBOX_OPO) {
        if (
          studiotel.url &&
          studiotel.login &&
          studiotel.password &&
          studiotel.phone_number
        ) {
          setStepValid(true);
        } else {
          setStepValid(false);
        }
      }
      if (studiotel.type === StudiotelConfig.MITEL_OFFICE_500) {
        if (
          studiotel.url &&
          studiotel.login &&
          studiotel.password &&
          studiotel.phone_number &&
          studiotel.offhook_tone
        ) {
          setStepValid(true);
        } else {
          setStepValid(false);
        }
      }
      if (studiotel.type === StudiotelConfig.PABX) {
        if (studiotel.dtmf) {
          setStepValid(true);
        } else {
          setStepValid(false);
        }
      }
      if (studiotel.type === StudiotelConfig.ALCATEL_OXE) {
        setStepValid(!!(
          studiotel.login &&
          studiotel.password &&
          studiotel.port &&
          studiotel.hostname &&
          studiotel.nb_att &&
          studiotel.nb_pre &&
          studiotel.nb_rep
        ));
      }
      if (studiotel.type === StudiotelConfig.ALCATEL_OXE_REDUNDANT) {
        const devices = studiotel.devices || [];
        const isDevice0Valid = !!devices[0] && isOxeDeviceValid(devices[0]);
        const isDevice1Valid = !!devices[1] && isOxeDeviceValid(devices[1]);
        setStepValid(isDevice0Valid && isDevice1Valid);
      }
      if (studiotel.type === StudiotelConfig.ALCATEL_OXO) {
        if (
          studiotel.login &&
          studiotel.password &&
          studiotel.hostname &&
          studiotel.nb_att &&
          studiotel.nb_pre &&
          studiotel.nb_rep
        ) {
          setStepValid(true);
        } else {
          setStepValid(false);
        }
      }
    } else {
      setStepValid(false);
    }
  }, [setStepValid, studiotelConfigFile]);

  const classes = useStyles();

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setStudiotelConfigFile({
      ...studiotelConfigFile,
      studiotel: { type: value as StudiotelConfig },
    });
    setStudiotelConfig(value as StudiotelConfig);
  }

  function handleRedundancyChange(event: React.ChangeEvent<HTMLInputElement>) {
    const checked = event.target.checked;
    const { studiotel } = studiotelConfigFile;

    if (checked) {
      // Migration des champs plats vers devices[0] + ajout d'un devices[1] vide
      setStudiotelConfigFile({
        ...studiotelConfigFile,
        studiotel: {
          type: StudiotelConfig.ALCATEL_OXE_REDUNDANT,
          devices: [
            {
              hostname: studiotel.hostname,
              port: studiotel.port,
              login: studiotel.login,
              password: studiotel.password,
              nb_att: studiotel.nb_att,
              nb_pre: studiotel.nb_pre,
              nb_rep: studiotel.nb_rep,
            },
            {},
          ],
        },
      });
    } else {
      // Migration de devices[0] vers les champs plats
      const device0 = studiotel.devices?.[0] || {};
      setStudiotelConfigFile({
        ...studiotelConfigFile,
        studiotel: {
          type: StudiotelConfig.ALCATEL_OXE,
          hostname: device0.hostname,
          port: device0.port,
          login: device0.login,
          password: device0.password,
          nb_att: device0.nb_att,
          nb_pre: device0.nb_pre,
          nb_rep: device0.nb_rep,
        },
      });
    }
  }

  return (
    <StepperLayout
      hintTitle={'Information de configuration'}
      hintContent={
        <Typography variant="caption" color="textSecondary" align="center">
          {`Veuillez choisir parmi la liste l'appareil qui sera relié à votre Studioplay. Remplisser les champs demandés puis appuyer sur le bouton VALIDER.`}
        </Typography>
      }
      hintAction={hintAction && hintAction}
      title={'Votre appareil à configurer :'}
      studiotelVersion={`Version du Studioplay : ${
        version ? version : 'inconnue'
      }`}
      action={action && action}
    >
      <div className={classes.container}>
        <FormControl component="fieldset">
          <RadioGroup
            name="type"
            value={studiotelConfig}
            onChange={handleChange}
          >
            {config.radioList.map((radioList: RadioList) => {
              const { Form } = config.form[radioList.value];
              const isOxe = radioList.value === StudiotelConfig.ALCATEL_OXE;

              return (
                <React.Fragment key={radioList.value}>
                  <FormControlLabel
                    value={radioList.value}
                    control={<Radio />}
                    label={radioList.label}
                    disabled={radioList.disabled}
                  />
                  {Form && studiotelConfig === radioList.value && (
                    <>
                      {isOxe && (
                        <FormControlLabel
                          className={classes.redundancyCheckbox}
                          control={
                            <Checkbox
                              checked={isOxeRedundant}
                              onChange={handleRedundancyChange}
                              color="primary"
                            />
                          }
                          label="Activer la configuration redondante"
                        />
                      )}
                      {isOxe && isOxeRedundant ? (
                        <div className={classes.formsRow}>
                          <div className={classes.formColumn}>
                            <Typography variant="caption" className={classes.deviceLabel}>
                              Configuration principale
                            </Typography>
                            <Form
                              configFile={studiotelConfigFile}
                              setConfigFile={setStudiotelConfigFile}
                              deviceIndex={0}
                              useDevices={true}
                            />
                          </div>
                          <div className={classes.formColumn}>
                            <Typography variant="caption" className={classes.deviceLabel}>
                              Configuration redondante
                            </Typography>
                            <Form
                              configFile={studiotelConfigFile}
                              setConfigFile={setStudiotelConfigFile}
                              deviceIndex={1}
                              useDevices={true}
                            />
                          </div>
                        </div>
                      ) : (
                        <Form
                          configFile={studiotelConfigFile}
                          setConfigFile={setStudiotelConfigFile}
                        />
                      )}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </RadioGroup>
        </FormControl>
      </div>
    </StepperLayout>
  );
}
